import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Role } from '../../types';
import { setHasDeletedKpi, setHasKpi, setRole } from '../../store/kpeSlice';
import { useKpeDispatch, useKpeSelector } from '../../store/store';
import styles from './DevPanel.module.scss';

interface RoleOption {
  value: Role;
  label: string;
  shortLabel: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  { value: 'Р', label: 'Руководитель', shortLabel: 'Р' },
  { value: 'ВР', label: 'Высший Руководитель', shortLabel: 'ВР' },
  { value: 'АС', label: 'УРП', shortLabel: 'УРП' },
  { value: 'OC', label: 'Оценивающее подразделение', shortLabel: 'ОС' }
];

interface PageLink {
  to: string;
  label: string;
}

const PAGE_LINKS: PageLink[] = [
  { to: '/kpe', label: 'Главная' },
  { to: '/kpe/planning', label: 'Планирование' },
  { to: '/kpe/facts', label: 'Факты' },
  { to: '/kpe/facts/input', label: 'Факты — Ввод данных' }
];

export function DevPanel() {
  const dispatch = useKpeDispatch();
  const role = useKpeSelector((s) => s.kpe.role);
  const hasKpi = useKpeSelector((s) => s.kpe.hasKpi);
  const hasDeletedKpi = useKpeSelector((s) => s.kpe.hasDeletedKpi);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);

  const isPlanning = location.pathname.startsWith('/kpe/planning');
  const isFactsInput = location.pathname.startsWith('/kpe/facts/input');
  const isFacts = location.pathname.startsWith('/kpe/facts') && !isFactsInput;

  const rolesDisabled = isFactsInput;
  const hasKpiAvailable = isPlanning;
  const hasDeletedAvailable = isPlanning && role === 'Р' && hasKpi;

  if (!isOpen) {
    return (
      <button
        type="button"
        className={styles.collapsed}
        onClick={() => setIsOpen(true)}
        aria-label="Открыть dev-панель"
      >
        <span className={styles.collapsedDot} />
        DEV · {role}
      </button>
    );
  }

  return (
    <div className={styles.panel} role="region" aria-label="Dev-панель демо">
      <div className={styles.header}>
        <span className={styles.title}>временно</span>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={() => setIsOpen(false)}
          aria-label="Свернуть"
        >
          ×
        </button>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Страницы</div>
        <div className={styles.links}>
          {PAGE_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              exact={link.to === '/kpe'}
              className={styles.link}
              activeClassName={styles.linkActive}
            >
              {link.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Роль
          {rolesDisabled && (
            <span className={styles.sectionHint}>
              не используется на этой странице
            </span>
          )}
        </div>
        <div
          className={clsx(styles.chips, {
            [styles.chipsDisabled]: rolesDisabled
          })}
        >
          {ROLE_OPTIONS.map((opt) => {
            const isAvailablePlan = !isPlanning || opt.value !== 'OC';
            const isDisabled = rolesDisabled || !isAvailablePlan;
            return (
              <button
                key={opt.value}
                type="button"
                className={clsx(styles.chip, {
                  [styles.chipActive]: role === opt.value && !rolesDisabled,
                  [styles.chipDisabled]: isDisabled
                })}
                onClick={() => !isDisabled && dispatch(setRole(opt.value))}
                title={
                  isDisabled ? 'Не используется на этой странице' : undefined
                }
              >
                <span className={styles.chipLabel}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Флаги</div>
        <div className={styles.toggles}>
          <label
            className={clsx(styles.toggle, {
              [styles.toggleDisabled]: !hasKpiAvailable
            })}
          >
            <input
              type="checkbox"
              checked={hasKpi}
              disabled={!hasKpiAvailable}
              onChange={(e) => dispatch(setHasKpi(e.target.checked))}
            />
            <span className={styles.toggleLabel}>
              Есть показатели КПЭ
              <span className={styles.toggleHint}>
                {hasKpiAvailable
                  ? 'есть показатели КПЭ'
                  : 'только на странице Планирования'}
              </span>
            </span>
          </label>

          <label
            className={clsx(styles.toggle, {
              [styles.toggleDisabled]: !hasDeletedAvailable
            })}
          >
            <input
              type="checkbox"
              checked={hasDeletedKpi}
              disabled={!hasDeletedAvailable}
              onChange={(e) => dispatch(setHasDeletedKpi(e.target.checked))}
            />
            <span className={styles.toggleLabel}>
              блок удалённых КПЭ
              <span className={styles.toggleHint}>
                {hasDeletedAvailable
                  ? 'блок удалённых КПЭ'
                  : 'только Планирование'}
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
